import React, { useState } from 'react';
import axios from 'axios';
import { Sparkles, Upload, Loader2, Check, Pencil, PackagePlus, AlertCircle } from 'lucide-react';
import InventoryExcelUploader from '@/components/InventoryExcelUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIFeatures = () => {
  const [prakritiData, setPrakritiData] = useState({
    symptoms: '',
    lifestyle: '',
    diet: '',
  });
  const [prakritiResult, setPrakritiResult] = useState('');
  const [prakritiLoading, setPrakritiLoading] = useState(false);

  // Purchase Bill Scanner state
  const [billFile, setBillFile] = useState(null);
  const [billScanLoading, setBillScanLoading] = useState(false);
  const [scannedItems, setScannedItems] = useState([]);
  const [vendorName, setVendorName] = useState('');
  const [billReference, setBillReference] = useState('');
  const [rawText, setRawText] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  const handlePrakritiSubmit = async (e) => {
    e.preventDefault();
    setPrakritiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/prakriti-analysis`, prakritiData);
      setPrakritiResult(response.data.analysis);
    } catch (error) {
      console.error('Error analyzing prakriti:', error);
      alert('Error analyzing prakriti: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPrakritiLoading(false);
    }
  };

  const handleBillScan = async (e) => {
    e.preventDefault();
    if (!billFile) {
      alert('Please select a file');
      return;
    }
    setBillScanLoading(true);
    setConfirmResult(null);
    setScannedItems([]);
    try {
      const formData = new FormData();
      formData.append('file', billFile);
      const response = await axios.post(`${API}/ai/scan-purchase-bill`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setScannedItems(response.data.items || []);
      setVendorName(response.data.vendorName || '');
      setBillReference(response.data.billReference || '');
      setRawText(response.data.rawText || '');
    } catch (error) {
      console.error('Error scanning bill:', error);
      alert('Error scanning bill: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBillScanLoading(false);
    }
  };

  const updateScannedItem = (index, field, value) => {
    const updated = [...scannedItems];
    if (field === 'quantity') {
      updated[index][field] = parseInt(value) || 0;
    } else if (field === 'mrp') {
      updated[index][field] = parseFloat(value) || 0;
    } else {
      updated[index][field] = value;
    }
    setScannedItems(updated);
  };

  const removeScannedItem = (index) => {
    setScannedItems(scannedItems.filter((_, i) => i !== index));
  };

  const addManualItem = () => {
    setScannedItems([
      ...scannedItems,
      { name: '', quantity: 0, mrp: 0, matchedMedicineId: null, isNew: true },
    ]);
    setEditingIndex(scannedItems.length);
  };

  const handleConfirmPurchase = async () => {
    if (scannedItems.length === 0) {
      alert('No items to update');
      return;
    }
    setConfirmLoading(true);
    try {
      const response = await axios.post(`${API}/medicines/confirm-purchase`, {
        items: scannedItems,
        vendorName,
        billReference,
      });
      setConfirmResult(response.data);
      setScannedItems([]);
      setBillFile(null);
    } catch (error) {
      console.error('Error confirming purchase:', error);
      alert('Error confirming purchase: ' + (error.response?.data?.detail || error.message));
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }} data-testid="ai-features-title">
          AI Features
        </h1>
        <p className="mt-2" style={{ color: '#6B7280' }}>AI-powered tools for Ayurvedic practice</p>
      </div>

      <InventoryExcelUploader />


      {/* PURCHASE BILL SCANNER - Full Width */}
      <div className="card p-6 mb-8" data-testid="purchase-bill-scanner-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-xl" style={{ backgroundColor: '#F5E6F5' }}>
            <Upload size={28} style={{ color: '#990099' }} />
          </div>
          <div>
            <h2 className="text-2xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
              Purchase Bill Scanner
            </h2>
            <p className="text-sm" style={{ color: '#6B7280' }}>
              Upload vendor purchase bills to auto-update medicine inventory
            </p>
          </div>
        </div>

        {/* Step 1: Upload */}
        {scannedItems.length === 0 && !confirmResult && (
          <form onSubmit={handleBillScan}>
            <div>
              <label>Upload Vendor Purchase Bill</label>
              <div
                className="border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer"
                style={{ borderColor: billFile ? '#990099' : '#E5E0D8', backgroundColor: billFile ? '#F5E6F5' : 'transparent' }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  setBillFile(e.dataTransfer.files[0]);
                }}
                onClick={() => document.getElementById('purchase-bill-input').click()}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setBillFile(e.target.files[0])}
                  className="hidden"
                  id="purchase-bill-input"
                  data-testid="purchase-bill-file-input"
                />
                <Upload size={48} style={{ color: '#990099', margin: '0 auto' }} />
                <p className="mt-4 font-medium" style={{ color: '#2D3A2A' }}>
                  {billFile ? billFile.name : 'Click to upload or drag and drop'}
                </p>
                <p className="text-sm mt-2" style={{ color: '#6B7280' }}>
                  Take a photo of the vendor bill or upload a scanned copy (PNG, JPG, WEBP)
                </p>
              </div>
            </div>
            <button
              type="submit"
              className="primary-btn w-full mt-6 flex items-center justify-center gap-2"
              disabled={billScanLoading || !billFile}
              style={{ opacity: billScanLoading || !billFile ? 0.5 : 1 }}
              data-testid="scan-purchase-bill-btn"
            >
              {billScanLoading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Scanning bill... This may take a moment
                </>
              ) : (
                <>
                  <Upload size={20} />
                  Scan Purchase Bill
                </>
              )}
            </button>
          </form>
        )}

        {/* Step 2: Review & Edit Extracted Items */}
        {scannedItems.length > 0 && !confirmResult && (
          <div data-testid="scanned-items-review">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold" style={{ color: '#2D3A2A' }}>
                  Review Extracted Items
                </h3>
                <p className="text-sm" style={{ color: '#6B7280' }}>
                  Edit any field before confirming. Changes will update your inventory.
                </p>
              </div>
              <button
                className="secondary-btn flex items-center gap-2"
                onClick={addManualItem}
                data-testid="add-manual-item-btn"
              >
                <PackagePlus size={18} />
                Add Item Manually
              </button>
            </div>

            {/* Vendor info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 rounded-xl" style={{ backgroundColor: '#F9F6F0' }}>
              <div>
                <label>Vendor / Supplier Name</label>
                <input
                  type="text"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  placeholder="Enter vendor name"
                  data-testid="vendor-name-input"
                />
              </div>
              <div>
                <label>Bill / Invoice Reference</label>
                <input
                  type="text"
                  value={billReference}
                  onChange={(e) => setBillReference(e.target.value)}
                  placeholder="Enter bill/invoice number"
                  data-testid="bill-reference-input"
                />
              </div>
            </div>

            {/* Items table */}
            <div className="overflow-x-auto mb-6">
              <table data-testid="scanned-items-table">
                <thead>
                  <tr>
                    <th style={{ width: '5%' }}>#</th>
                    <th style={{ width: '35%' }}>Medicine Name</th>
                    <th style={{ width: '15%' }}>Quantity</th>
                    <th style={{ width: '15%' }}>MRP (per unit)</th>
                    <th style={{ width: '15%' }}>Status</th>
                    <th style={{ width: '15%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scannedItems.map((item, index) => (
                    <tr key={index} data-testid={`scanned-item-row-${index}`}>
                      <td style={{ color: '#6B7280' }}>{index + 1}</td>
                      <td>
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateScannedItem(index, 'name', e.target.value)}
                          className="font-medium"
                          style={{ border: editingIndex === index ? '2px solid #990099' : '1px solid #E5E0D8' }}
                          data-testid={`item-name-${index}`}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateScannedItem(index, 'quantity', e.target.value)}
                          min="0"
                          style={{ border: editingIndex === index ? '2px solid #990099' : '1px solid #E5E0D8' }}
                          data-testid={`item-quantity-${index}`}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={item.mrp}
                          onChange={(e) => updateScannedItem(index, 'mrp', e.target.value)}
                          min="0"
                          style={{ border: editingIndex === index ? '2px solid #990099' : '1px solid #E5E0D8' }}
                          data-testid={`item-mrp-${index}`}
                        />
                      </td>
                      <td>
                        {item.isNew ? (
                          <span
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}
                          >
                            <PackagePlus size={14} />
                            New Item
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}
                          >
                            <Check size={14} />
                            Matched
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                            className="p-2 rounded hover:bg-gray-100 transition-colors"
                            title="Edit"
                            data-testid={`edit-item-${index}`}
                          >
                            <Pencil size={16} style={{ color: '#990099' }} />
                          </button>
                          <button
                            onClick={() => removeScannedItem(index)}
                            className="p-2 rounded hover:bg-red-50 transition-colors"
                            title="Remove"
                            data-testid={`remove-item-${index}`}
                          >
                            <AlertCircle size={16} style={{ color: '#EF4444' }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: '#F9F6F0', border: '1px solid #E5E0D8' }}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm" style={{ color: '#78716C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Items</p>
                  <p className="text-2xl font-bold" style={{ color: '#2D3A2A' }} data-testid="total-items-count">{scannedItems.length}</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: '#78716C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Existing (Update Stock)</p>
                  <p className="text-2xl font-bold" style={{ color: '#990099' }}>{scannedItems.filter(i => !i.isNew).length}</p>
                </div>
                <div>
                  <p className="text-sm" style={{ color: '#78716C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>New (Will be Added)</p>
                  <p className="text-2xl font-bold" style={{ color: '#1E40AF' }}>{scannedItems.filter(i => i.isNew).length}</p>
                </div>
              </div>
            </div>

            {/* Info banner */}
            <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A' }}>
              <div className="flex items-start gap-3">
                <AlertCircle size={20} style={{ color: '#D97706', flexShrink: 0, marginTop: 2 }} />
                <div>
                  <p className="font-medium text-sm" style={{ color: '#92400E' }}>Before confirming:</p>
                  <ul className="text-sm mt-1 space-y-1" style={{ color: '#92400E' }}>
                    <li>Existing medicines will have their <strong>stock quantity increased</strong> and <strong>MRP updated</strong></li>
                    <li>New items will be added to the inventory with a default reorder level of 10</li>
                    <li>You can edit any field above before saving</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleConfirmPurchase}
                className="primary-btn flex items-center gap-2 px-6"
                disabled={confirmLoading}
                style={{ opacity: confirmLoading ? 0.5 : 1 }}
                data-testid="confirm-purchase-btn"
              >
                {confirmLoading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Updating Inventory...
                  </>
                ) : (
                  <>
                    <Check size={20} />
                    Confirm & Update Inventory
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setScannedItems([]);
                  setBillFile(null);
                  setVendorName('');
                  setBillReference('');
                }}
                className="secondary-btn"
                data-testid="cancel-purchase-btn"
              >
                Cancel
              </button>
            </div>

            {/* Raw AI response (collapsible) */}
            {rawText && (
              <details className="mt-6">
                <summary className="cursor-pointer font-medium text-sm" style={{ color: '#990099' }}>
                  View Raw AI Response
                </summary>
                <div className="mt-2 p-4 rounded bg-gray-50 text-sm" style={{ color: '#2D3A2A' }}>
                  {rawText}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Step 3: Success Confirmation */}
        {confirmResult && (
          <div data-testid="purchase-success">
            <div className="p-6 rounded-xl text-center" style={{ backgroundColor: '#D1FAE5' }}>
              <Check size={48} style={{ color: '#065F46', margin: '0 auto' }} />
              <h3 className="text-xl font-bold mt-4" style={{ color: '#065F46' }}>
                Inventory Updated Successfully!
              </h3>
              <p className="mt-2" style={{ color: '#065F46' }}>
                {confirmResult.message}
              </p>
              <div className="mt-4 space-y-2">
                {confirmResult.results && confirmResult.results.map((r, i) => (
                  <div key={i} className="inline-flex items-center gap-2 bg-white px-3 py-1 rounded-full text-sm mr-2">
                    <span className="font-medium" style={{ color: '#2D3A2A' }}>{r.medicine?.name}</span>
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: r.action === 'created' ? '#DBEAFE' : '#D1FAE5',
                        color: r.action === 'created' ? '#1E40AF' : '#065F46',
                      }}
                    >
                      {r.action === 'created' ? 'Added' : 'Updated'}
                    </span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  setConfirmResult(null);
                  setBillFile(null);
                  setVendorName('');
                  setBillReference('');
                  setRawText('');
                }}
                className="primary-btn mt-6"
                data-testid="scan-another-btn"
              >
                Scan Another Bill
              </button>
            </div>
          </div>
        )}
      </div>

      {/* PRAKRITI ANALYSIS */}
      <div className="card p-6" data-testid="prakriti-analysis-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-xl" style={{ backgroundColor: '#F5E6F5' }}>
            <Sparkles size={28} style={{ color: '#990099' }} />
          </div>
          <div>
            <h2 className="text-2xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
              Prakriti Analysis
            </h2>
            <p className="text-sm" style={{ color: '#6B7280' }}>AI-powered constitution analysis</p>
          </div>
        </div>

        <form onSubmit={handlePrakritiSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label>Symptoms</label>
              <textarea
                value={prakritiData.symptoms}
                onChange={(e) => setPrakritiData({ ...prakritiData, symptoms: e.target.value })}
                rows={4}
                placeholder="Describe the patient's symptoms..."
                required
                data-testid="prakriti-symptoms-input"
              />
            </div>
            <div>
              <label>Lifestyle</label>
              <textarea
                value={prakritiData.lifestyle}
                onChange={(e) => setPrakritiData({ ...prakritiData, lifestyle: e.target.value })}
                rows={4}
                placeholder="Describe lifestyle patterns..."
                required
                data-testid="prakriti-lifestyle-input"
              />
            </div>
            <div>
              <label>Diet</label>
              <textarea
                value={prakritiData.diet}
                onChange={(e) => setPrakritiData({ ...prakritiData, diet: e.target.value })}
                rows={4}
                placeholder="Describe dietary habits..."
                required
                data-testid="prakriti-diet-input"
              />
            </div>
          </div>
          <button
            type="submit"
            className="primary-btn mt-6 flex items-center justify-center gap-2"
            disabled={prakritiLoading}
            style={{ opacity: prakritiLoading ? 0.5 : 1 }}
            data-testid="prakriti-analyze-btn"
          >
            {prakritiLoading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles size={20} />
                Analyze Prakriti
              </>
            )}
          </button>
        </form>

        {prakritiResult && (
          <div className="mt-6 p-4 rounded-xl" style={{ backgroundColor: '#F5E6F5' }} data-testid="prakriti-result">
            <h3 className="font-bold mb-2" style={{ color: '#2D3A2A' }}>Analysis Result:</h3>
            <div className="whitespace-pre-wrap" style={{ color: '#2D3A2A' }}>
              {prakritiResult}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIFeatures;
